// interfaces/types
import type { StateTree } from '@makemydeal/dr-dash-types';

/**
 * Fail-open registry for the payment sequence guard (`paymentSequenceMiddleware`).
 *
 * WHY THIS EXISTS
 * The guard discards a superseded payment response by refusing to forward it, which also hides it from
 * every middleware downstream. Most `PAYMENT_RECEIVED` reactors discriminate on the *action* — either
 * `actionForPayment === SOME_TYPE` or `originalAction.type === SOME_TYPE` — so a static scan can find
 * them and `paymentSequenceExemptList.spec.ts` forces each one onto an explicit list.
 *
 * A minority do not: they key on STATE (`vehicleProtectionMiddleware`'s contract-validation refresh, the
 * Balloon → UPQ fallback in dash `paymentMiddleware`), so they can fire for *any* payment. No action-type
 * exemption can express that and no grep can find it. Those register a discriminating selector instead —
 * see `paymentSequencePendingOperations.ts`, which holds every registration and is imported by
 * `paymentSequenceMiddleware` so that installing the guard is what populates this registry. Before
 * discarding anything the guard evaluates every registered selector; any `true` means some operation is
 * waiting on this response, so the guard forwards it rather than hanging that operation.
 *
 * A selector registered here is consulted for EVERY superseded response, so it must be true only while its
 * operation is genuinely in flight. A broadly-true selector disables the guard wholesale for that window,
 * which is why `incompleteTradeModalMiddleware` (`getBypassIncompleteTradeCheck`) deliberately does NOT
 * register despite also being state-keyed: it runs upstream of the guard and acts before its own `next()`,
 * so a discard cannot hide anything from it. See the README in this directory.
 *
 * THE TRADE, MADE DELIBERATELY
 * A pass-through means a stale response *does* reach the reducers, so stale values can render. That is
 * accepted: a wrong number the next response corrects is recoverable, a hung slideout is not. An
 * operation nobody remembered to register degrades to today's behavior plus a telemetry event, never to
 * a hang.
 *
 * WHY ITS OWN MODULE
 * Registration and the guard sit in the same middleware layer, and the registering side has to reach
 * selectors. Keeping the registry itself down to a single type import means `pnpm run check:circular` stays
 * green no matter who registers or from where.
 */
export type PendingOperationSelector = (state: StateTree) => boolean;

const pendingOperationSelectors = new Map<string, PendingOperationSelector>();

/**
 * Declare that an in-flight operation depends on seeing `PAYMENT_RECEIVED` / `PAYMENT_FAILURE` even when
 * the response has been superseded. Called at module load from `paymentSequencePendingOperations.ts`.
 *
 * `name` identifies the selector in the `PaymentSequencePassThrough` New Relic event, so it should name
 * the operation ('bypassIncompleteTrade'), not the selector function. Re-registering a name overwrites,
 * which keeps module re-evaluation under Jest idempotent.
 */
export const registerPendingOperationSelector = (name: string, selector: PendingOperationSelector): void => {
    pendingOperationSelectors.set(name, selector);
};

/**
 * Returns the name of the first registered selector that reports a pending operation, or `undefined`
 * when none do. Returning the *name* rather than a boolean is what lets the guard attribute the
 * pass-through in telemetry — a new name appearing in production is how a missed exemption announces
 * itself.
 *
 * A throwing selector counts as "no pending operation": a broken selector must not be able to disable the
 * guard wholesale, and the guard's own error path would be the wrong place to surface it.
 *
 * But it must not be SILENT about it either. This is the one place where the net's promise ("never a hang")
 * inverts: a selector that throws while its operation is genuinely pending sends the guard on to discard the
 * response that operation was waiting on, and the workflow hangs with no event to explain why. The `console`
 * line is that explanation. It stays out of production for the reason the pass-through's does — a payment can
 * resolve many times a minute and a broken selector would throw on every one — where the New Relic side is
 * covered by the guard emitting no `PaymentSequencePassThrough` for a name that used to appear.
 */
export const findPendingOperation = (state: StateTree): string | undefined => {
    for (const [name, selector] of pendingOperationSelectors) {
        try {
            if (selector(state)) return name;
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.error(
                    `paymentSequenceRegistry: pending-operation selector "${name}" threw and was read as "nothing ` +
                        `pending", so paymentSequenceMiddleware may discard a response that operation needs. ` +
                        `Guard the state it reads (see the pre-hydration guard in paymentSequencePendingOperations.ts).`,
                    error
                );
            }
        }
    }
    return undefined;
};

/** Test-only. Registration happens at module load, so specs need a way back to a clean slate. */
export const clearPendingOperationSelectors = (): void => {
    pendingOperationSelectors.clear();
};

/** Test-only. Lets the meta-test assert which operations are actually registered. */
export const getRegisteredPendingOperationNames = (): string[] => [...pendingOperationSelectors.keys()];
